import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FiMessageCircle, FiX, FiSend } from 'react-icons/fi';
import { theme } from '../theme';
import LevelBadge from './LevelBadge';
import VoiceBar from './VoiceBar';
import EmojiPicker from './EmojiPicker';
import ChatMessageBody, { parseContent, isControl } from './ChatMessageBody';
import ChatAuthor from './ChatAuthor';
import VoiceRecorder from './VoiceRecorder';
import { stickerEffect } from '../services/chatFun';
import { useChatSocial, ReactBtn, Reactions } from './ChatSocial';
import { nakama } from '../config/nakama';
import { useAuth } from '../context/AuthContext';

/* Filtre anti-insultes minimal (masque quelques termes) — modération de base. */
const BAD = ['connard', 'salope', 'pute', 'enculé', 'ntm', 'fdp', 'nique', 'batard', 'bâtard'];
function clean(txt) {
  let t = txt;
  BAD.forEach((w) => { t = t.replace(new RegExp(w, 'gi'), '*'.repeat(w.length)); });
  return t.slice(0, 280);
}

function pseudo() {
  const k = 'vg_pseudo';
  let p = null; try { p = localStorage.getItem(k); } catch { /* */ }
  if (!p) { p = 'Joueur' + Math.floor(1000 + Math.random() * 9000); try { localStorage.setItem(k, p); } catch { /* */ } }
  return p;
}

const Fab = styled.button`
  position: fixed; bottom: 1.2rem; right: 1.2rem; z-index: 1500;
  display: inline-flex; align-items: center; gap: .5rem; white-space: nowrap;
  /* Mobile : la languette flottante est masquée (le bouton « Social » prend sa place ; le chat s'ouvre
     depuis le menu de navigation gauche). */
  @media (max-width: 767px) { display: none; }
  padding: .6rem 1rem; border-radius: 999px; border: none; cursor: pointer; font-weight: 700; font-size: .86rem;
  background: ${theme.colors.gradient}; color: #fff;
  box-shadow: ${theme.shadows.glow}; transition: ${theme.transitions.default};
  &:hover { transform: scale(1.05); }
`;
const Panel = styled.div`
  position: fixed; bottom: 1.2rem; right: 1.2rem; z-index: 1601;   /* au-dessus du bouton Social (1500) quand ouvert */
  width: min(360px, calc(100vw - 2rem)); height: 460px; max-height: calc(100vh - 2rem);
  display: flex; flex-direction: column; overflow: hidden;
  background: ${theme.colors.bgCard}; border: 1px solid ${theme.colors.borderHover};
  border-radius: ${theme.radii.lg}; box-shadow: ${theme.shadows.card};
`;
const Head = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.8rem 1rem; background: ${theme.colors.gradient}; color: #fff;
  h4 { font-family: ${theme.fonts.heading}; font-size: 0.95rem; display: flex; align-items: center; gap: 0.4rem; }
  .st { font-size: 0.68rem; opacity: 0.9; }
  button { background: none; border: none; color: #fff; cursor: pointer; font-size: 1.1rem; display: flex; }
`;
const Body = styled.div`flex: 1; overflow-y: auto; padding: 0.8rem; display: flex; flex-direction: column; gap: 0.5rem;`;
const Msg = styled.div`
  font-size: 0.85rem; line-height: 1.4;
  .u { color: ${({ $me }) => ($me ? theme.colors.secondary : theme.colors.primaryLight)}; font-weight: 700; }
  .t { color: ${theme.colors.textSecondary}; }
`;
const Foot = styled.form`display: flex; gap: 0.4rem; padding: 0.6rem; border-top: 1px solid ${theme.colors.border};`;
const In = styled.input`
  flex: 1; background: ${theme.colors.bgPrimary}; color: ${theme.colors.textPrimary};
  border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.full}; padding: 0.5rem 0.9rem; font-size: 0.85rem;
  &:focus { outline: none; border-color: ${theme.colors.borderHover}; }
`;
const Btn = styled.button`
  border: none; cursor: pointer; background: ${theme.colors.gradient}; color: #fff;
  width: 40px; border-radius: ${theme.radii.full}; display: flex; align-items: center; justify-content: center;
`;
const Info = styled.p`color: ${theme.colors.textMuted}; font-size: 0.78rem; text-align: center; margin: auto 0;`;

export default function ChatWidget() {
  const { auth, user, isPremium } = useAuth();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('connexion…');
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [pick, setPick] = useState(false);
  const social = useChatSocial();
  const typingRef = useRef(0);
  const channelRef = useRef(null);
  const bodyRef = useRef(null);
  // Identité = nom de l'espace membre (cohérent avec le compte), pseudo aléatoire en dernier recours.
  const me = (user && (user.display_name || user.username)) || pseudo();

  useEffect(() => {
    if (!open || !nakama.enabled || channelRef.current) return;
    let cancelled = false;
    let unsub = null;
    (async () => {
      // Réutilise la session Nakama de l'espace membre (même identité que le compte).
      const channel = await auth.joinChannel('vertingo-global', 1);
      if (cancelled) return;
      if (!channel) { setStatus('indisponible'); return; }
      channelRef.current = channel;
      // Charge l'historique récent (messages persistés) → on voit le fil même en arrivant après.
      try {
        const hist = await auth.listChatHistory(channel.id, 30);
        if (!cancelled && hist.length) {
          setMsgs(hist.map((m) => {
            const c = parseContent(m.content);
            return { id: m.message_id, user: m.username || 'joueur', sender: m.sender_id, text: c.text, sticker: c.sticker, audio: c.audio, gif: c.gif, premium: c.premium };
          }).filter((x) => x.text || x.sticker || x.gif || x.audio));
        }
      } catch { /* pas d'historique → on démarre vide */ }
      unsub = auth.onChatMessage((m) => {
        const c = parseContent(m.content);
        // Messages de CONTRÔLE (réaction / « écrit… ») → pilotent l'UI, ne s'affichent pas dans le fil.
        if (isControl(c)) { social.handleControl(c, m.username, m.sender_id === (auth.session && auth.session.user_id)); return; }
        if (!c.text && !c.sticker && !c.gif && !c.audio) return;
        if (c.sticker) stickerEffect(c.sticker); // 🧨 secousse / 🏆 confettis à la réception
        setMsgs((prev) => [...prev.slice(-80), { id: m.message_id, user: m.username || 'joueur', sender: m.sender_id, text: c.text, sticker: c.sticker, audio: c.audio, gif: c.gif, premium: c.premium }]);
      });
      setStatus('en ligne');
    })();
    // Reconnexion socket (retour d'onglet/bfcache) → on rejoint le canal global (sinon envoi KO).
    const offReconnect = auth.onReconnect(async () => {
      const channel = await auth.joinChannel('vertingo-global', 1);
      if (channel && !cancelled) channelRef.current = channel;
    });
    return () => { cancelled = true; if (unsub) unsub(); if (offReconnect) offReconnect(); };
  }, [open, auth]);

  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [msgs]);

  // Ouverture DEPUIS le drawer de navigation (mobile) : sur mobile la languette flottante « Chat communauté »
  // est masquée (remplacée par le bouton Social), le chat s'ouvre via l'entrée du menu gauche → événement global.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('vg-open-chat', onOpen);
    return () => window.removeEventListener('vg-open-chat', onOpen);
  }, []);

  const send = async (e) => {
    e.preventDefault();
    const t = clean(text.trim());
    if (!t || !channelRef.current) return;
    const ok = await auth.sendChat(channelRef.current.id, t, isPremium ? { premium: true } : {});
    if (ok) setText('');
  };
  const sendExtra = async (extra) => {
    if (!channelRef.current) return;
    await auth.sendChat(channelRef.current.id, '', isPremium ? { premium: true, ...extra } : extra);
  };
  const sendSticker = (id) => sendExtra({ sticker: id });
  const reactTo = (msgId, emoji) => { if (channelRef.current) { auth.sendChat(channelRef.current.id, '', { react: emoji, ref: msgId }); social.bump(msgId, emoji); } };
  const notifyTyping = () => { const now = Date.now(); if (channelRef.current && now - typingRef.current > 2500) { typingRef.current = now; auth.sendChat(channelRef.current.id, '', { typing: true }); } };

  if (!nakama.enabled) return null;

  if (!open) return <Fab onClick={() => setOpen(true)} aria-label="Chat communauté"><FiMessageCircle /> Chat communauté</Fab>;

  return (
    <Panel>
      <Head>
        <h4><FiMessageCircle /> Chat communauté <span className="st">· {status}</span></h4>
        <button onClick={() => setOpen(false)} aria-label="Fermer"><FiX /></button>
      </Head>
      <div style={{ padding: '0 .8rem' }}><VoiceBar channelName="vgvoice_general" label="le vocal général" /></div>
      <Body ref={bodyRef}>
        {msgs.length === 0
          ? <Info>Sois le premier à dire bonjour 👋<br />(pseudo : {me})</Info>
          : msgs.map((m) => (
            <Msg key={m.id} $me={m.sender === (auth.session && auth.session.user_id)}>
              <span className="u"><ChatAuthor senderId={m.sender} fallback={m.user} size={18} />{m.premium && <span title="Membre Premium" style={{ marginLeft: 3 }}>👑</span>}<LevelBadge userId={m.sender} /></span> <span className="t"><ChatMessageBody text={m.text} sticker={m.sticker} audio={m.audio} gif={m.gif} /></span>
              <Reactions data={social.reactions[m.id]} /><ReactBtn onReact={(e) => reactTo(m.id, e)} />
            </Msg>
          ))}
      </Body>
      {social.typingName && <div style={{ padding: '0 .8rem .3rem', fontSize: '.74rem', color: theme.colors.textMuted, fontStyle: 'italic' }}>{social.typingName} écrit…</div>}
      <Foot onSubmit={send}>
        <span style={{ position: 'relative', display: 'inline-flex' }}>
          <Btn type="button" aria-label="Emojis & stickers" onClick={() => setPick((p) => !p)} style={{ background: 'transparent', color: theme.colors.textSecondary }}>😊</Btn>
          {pick && <EmojiPicker onEmoji={(e) => setText((t) => (t + e).slice(0, 280))} onSticker={(id) => { sendSticker(id); setPick(false); }} onGif={(url) => { sendExtra({ gif: url }); setPick(false); }} onClose={() => setPick(false)} />}
        </span>
        <VoiceRecorder onAudio={(url) => sendExtra({ audio: url })} />
        <In value={text} onChange={(e) => { setText(e.target.value); notifyTyping(); }} placeholder="Ton message…" maxLength={280} />
        <Btn type="submit" aria-label="Envoyer"><FiSend /></Btn>
      </Foot>
    </Panel>
  );
}
